
import { skills, tools } from '../../mocks/portfolio';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export const testimonials = [
  {
    id: 1,
    name: "Matt Snyder",
    role: "VP of Product Design at Axiom",
    content: "As a product designer at Axiom, Wade led design efforts for 1 of our 3 digital platform experiences. He created a strong partnership with his product manager and added business and user value to all the features he worked on and the problems he solved. Through user research, data, and a strong understanding of business process, Wade took that area of our product from 0-100. Wade's hard work and determination is hard to find. His positive attitude helped push our team forward as our team and department grew over the last 2 years!",
    avatar: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/968110452452bf4aaef6cfc51f131185.png"
  },
  {
    id: 2,
    name: "John Hurr",
    role: "Director of User Experience at Sonos",
    content: "Wade has an excellent attitude and produces high quality work. He works well independently and with others. He has a great eye for design and sketches out most ideas before execution - a definite plus. Wade is a solid designer, I would recommend him to anyone.",
    avatar: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/6d9f19e2317c14f5d064ee6d3201eaf1.png"
  },
  {
    id: 3,
    name: "Yevhen Tsvietukhin",
    role: "Head of Product at Sched",
    content: "I highly, highly recommend Wade. He is one of the most exceptional Senior Product Designers I've worked with in my 15+ year career. Wade combines deep product thinking with outstanding execution. His Figma expertise is among the best I've seen: he delivers high-quality screens rapidly, with incredible attention to detail and consistency. He conducts thorough research on competitors, user behavior, and relevant UX patterns, ensuring each solution is grounded in real customer needs.",
    avatar: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/2e48deea3f711ac3f6fe63f34777e818.png"
  }
];

export default function About() {
  return (
    <div className="min-h-screen bg-gray-950 text-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                About Me
              </h1>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                I&apos;m Wade Ambrose, a Senior Product/UX Designer with 10+ years of experience creating digital experiences that matter. 
                My passion lies in solving complex problems through thoughtful design and user-centered thinking.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                I believe great design is invisible – it just works. My approach combines strategic thinking, 
                user research, and creative execution to deliver products that users love and businesses value.
              </p>
              <Link to="/contact">
                <Button variant="primary" size="lg">
                  Let&apos;s Work Together
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
            </div>
            
            {/* Hero Image */}
            <div className="relative flex justify-center">
              <div className="aspect-[3/4] overflow-hidden rounded-xl w-4/5">
                <img 
                  src="https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/fd0e9491b7fddc2419cbb5c8a5312f4c.png"
                  alt="Wade Ambrose"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">My Journey</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {[
                {
                  year: "2023 - 2025",
                  role: "Product Designer",
                  company: "Sched",
                  description: "Led end‑to‑end design for Sched's event platform, enhancing experiences for organizers, attendees, and speakers. Increased attendee NPS from 12 to 30 and reduced customer churn by about 10% through targeted UX improvements. Built and refined features like badges and QR check‑ins, and created a scalable design system with workflow best practices to improve efficiency and consistency across the organization."
                },
                {
                  year: "2018 - 2023",
                  role: "UX/Product Designer",
                  company: "Axiom Law",
                  description: "Led design for Axiom's talent marketplace, creating a job board that surpassed its annual goal by 170%. Designed hiring systems, built a unified design system, and drove research to guide strategy while supporting Axiom's shift to a digital-first organization."
                },
                {
                  year: "2015 - 2017",
                  role: "Lead UX/UI Designer",
                  company: "INRIX",
                  description: "Lead designer for OpenCar in-dash platform. Developed and maintained a flexible design system with components, templates, and accessibility standards, customized per automaker. Designed INRIX Parking in-app, boosting revenue from $1M to $8M."
                },
                {
                  year: "2014 - 2015",
                  role: "Interactive Designer",
                  company: "Hipcricket",
                  description: "Led design team creating banner, interstitial, and rich media ads for clients including McDonald's, Dodge, IKEA, and Wingstop. Supported sales with new ad concepts, boosting revenue, and expanded Hipcricket's portfolio."
                },
                {
                  year: "2013 - 2014",
                  role: "UI/UX Designer",
                  company: "Ambrose Design",
                  description: "Redesigned Intelliuse's reverse phone app and launched Nintendo's Tomodachi Life website as production designer."
                },
                {
                  year: "2010 - 2013",
                  role: "UI/UX Designer",
                  company: "Ubermind",
                  description: "Designed mobile apps for Alaska Airlines, Target, Showtime, and Fox Sports, including leading the Fox Sports Android app and UFC Game Trax UI/UX. Created pitch prototypes for Cable Vision and Starbucks."
                }
              ].map((experience, index) => (
                <div key={index} className="flex gap-8">
                  <div className="flex-shrink-0 w-32 text-purple-400 font-medium">
                    {experience.year}
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-white mb-2">{experience.role}</h3>
                    <p className="text-lg text-purple-400 mb-3">{experience.company}</p>
                    <p className="text-white leading-relaxed">{experience.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Tools */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Skills */}
            <div>
              <h3 className="text-3xl font-bold mb-8">Core Skills</h3>
              <div className="space-y-6">
                {skills.map((skill) => (
                  <div key={skill.name} className="bg-gray-800 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">{skill.name}</span>
                      <span className="text-purple-400 text-sm">
                        {skill.level >= 90 ? 'Expert' : 
                         skill.level >= 85 ? 'Advanced' : 
                         skill.level >= 80 ? 'Proficient' : 'Intermediate'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tools */}
            <div>
              <h3 className="text-3xl font-bold mb-8">Tools &amp; Technologies</h3>
              <div className="grid grid-cols-2 gap-4">
                {tools.map((tool) => (
                  <div 
                    key={tool}
                    className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-colors duration-300"
                  >
                    <span className="text-white font-medium">{tool}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-gray-800 rounded-xl">
                <h4 className="text-xl font-bold text-white mb-4">Design Philosophy</h4>
                <p className="text-gray-400 leading-relaxed">
                  "Design is not just what it looks like and feels like. Design is how it works." 
                  I believe in creating solutions that are not only beautiful but also functional, 
                  accessible, and meaningful to the people who use them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">What People Say</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-colors duration-300"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.id === 1 ? "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/968110452452bf4aaef6cfc51f131185.png" : testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    {testimonial.id === 1 ? (
                      <a 
                        href="https://www.linkedin.com/in/snyder-ux-leader/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white font-semibold hover:text-purple-400 transition-colors duration-300 cursor-pointer"
                      >
                        {testimonial.name}
                      </a>
                    ) : testimonial.id === 2 ? (
                      <a 
                        href="https://www.linkedin.com/in/johnhurr/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white font-semibold hover:text-purple-400 transition-colors duration-300 cursor-pointer"
                      >
                        {testimonial.name}
                      </a>
                    ) : (
                      <a 
                        href="https://www.linkedin.com/in/yevhen-tsvietukhin-6662532/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-white font-semibold hover:text-purple-400 transition-colors duration-300 cursor-pointer"
                      >
                        {testimonial.name}
                      </a>
                    )}
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed">"{testimonial.content}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900 via-gray-900 to-pink-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Let&apos;s Create Something Great Together
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            I&apos;m always excited to work on new challenges and collaborate with passionate teams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="primary" size="lg">
                Get In Touch
                <i className="ri-mail-line ml-2"></i>
              </Button>
            </Link>
            <Link to="/work">
              <Button variant="secondary" size="lg">
                View My Work
                <i className="ri-eye-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
